import { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faEllipsis,
  faPenToSquare,
  faCircleMinus,
  faPlus,
  faArrowDownShortWide,
  faArrowDownWideShort,
  faSearch,
} from "@fortawesome/pro-regular-svg-icons";
import { useNavigate } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import { useAuth } from "../../providers/AuthContext";
import { getOrganizationDisplayName } from "../../helpers/CustomerDisplay";
import CustomDropdown from "../../components/CustomDropdown";
import InputField from "../../components/InputField"; 
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import { getAllTariffs, deleteTariff } from "../../api/Tariffs"; 
import { useTranslation } from "react-i18next";

const TariffsPage = () => {
  const { t } = useTranslation();
  const { getAccessToken } = useAuth();
  const showOrganization = false;
  const [tariffs, setTariffs] = useState([]);
  const [filteredTariffs, setFilteredTariffs] = useState([]);
  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });
  const navigate = useNavigate();
  const [tariffsLoaded, setTariffsLoaded] = useState(false);

  useEffect(() => {
    handleGetTariffs();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional effect scope
  }, []);

  // Combined filtering and sorting logic into one useEffect
  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = tariffs;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = tariffs.filter((tariff) => {
          const organizationName = getOrganizationDisplayName(tariff).toLowerCase();
          return (
            tariff.tariffPublicId.toLowerCase().includes(searchLower) ||
            tariff.tariffName?.toLowerCase().includes(searchLower) ||
            organizationName.includes(searchLower)
          );
        });
      }

      const sorted = [...filtered].sort((a, b) => {
        let aValue = a[sortColumn]?.toString().toLowerCase();
        let bValue = b[sortColumn]?.toString().toLowerCase();

        if (sortOrder === "asc") {
          return aValue > bValue ? 1 : -1;
        } else {
          return aValue < bValue ? 1 : -1;
        }
      });

      setFilteredTariffs(sorted);
    };

    applyFilterAndSort();
  }, [search, tariffs, sortColumn, sortOrder]);

  const handleGetTariffs = async () => {
    try {
      const accessToken = await getAccessToken();
      const newTariff = (await getAllTariffs(accessToken)).map((tariff) => ({
        ...tariff,
        organizationName: getOrganizationDisplayName(tariff),
      }));
      setTariffs(newTariff);
      setFilteredTariffs(newTariff);
      setTariffsLoaded(true);
    } catch (error) {
      console.error(error);
    }
  };

    const handleDeleteTariff = async (tariffPublicId) => {
    const toastId = toast.loading(t("toast.deleting", { entity: t("entity.tariff") }));
    try {
      const accessToken = await getAccessToken();
      await deleteTariff(tariffPublicId, accessToken);
      const newTariff = (await getAllTariffs(accessToken)).map((tariff) => ({
        ...tariff,
        organizationName: getOrganizationDisplayName(tariff),
      }));
      setTariffs(newTariff);
      toast.success(t("toast.deleted", { entity: t("entity.tariff") }), { id: toastId });
    } catch (error) {
      toast.error(error?.message ?? t("toast.failedDelete", { entity: t("entity.tariff") }), { id: toastId });
      console.error(error);
    }
  };

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const getSortIcon = (column) => {
    if (sortColumn === column) {
      return sortOrder === "asc" ? faArrowDownShortWide : faArrowDownWideShort;
    }
    return null;
  };

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute inset-0 pointer-events-none">
              <LoadingTablePage title={t("entity.tariffs")} loading={!tariffsLoaded} />
            </div>

            <div className={`${!tariffsLoaded ? "loading" : ""} page-content`}>
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">{t("entity.tariffs")}</h2>
                <button
                  className="btn btn-primary"
                  onClick={() => {
                    navigate(`/tariffs/create`);
                  }}
                >
                  <FontAwesomeIcon icon={faPlus} /> {t("pages.createTariff")}
                </button>
              </div>
              <div className="rounded-md shadow-md w-full p-4">
                <div className="flex w-full justify-end">
                  <div className="mb-2">
                    <InputField
                      icon={faSearch}
                      placeholder={t("search.tariffs")}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                  </div>
                </div>
                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <table className="table-default w-full">
                    <thead>
                      <tr>
                        <th
                          className="text-center cursor-pointer"
                          onClick={() => handleSort("tariffId")}
                        >
                          {t("table.tariffId")}
                          {getSortIcon("tariffId") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("tariffId")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th
                          className="text-center cursor-pointer"
                          onClick={() => handleSort("tariffName")}
                        >
                          {t("common.name")}
                          {getSortIcon("tariffName") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("tariffName")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        {showOrganization && (
                          <th
                            className="text-center cursor-pointer"
                            onClick={() => handleSort("organizationName")}
                          >
                            {t("entity.organization")}
                            {getSortIcon("organizationName") && (
                              <FontAwesomeIcon
                                icon={getSortIcon("organizationName")}
                                className="w-4 pl-2"
                              />
                            )}
                          </th>
                        )}
                        <th
                          className="text-center cursor-pointer"
                          onClick={() => handleSort("tariffPublicId")}
                        >
                          {t("table.countryCode")}
                          {getSortIcon("countryCode") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("countryCode")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th
                          className="text-center cursor-pointer"
                          onClick={() => handleSort("countryCode")}
                        >
                          {t("common.currency")}{" "}
                          {getSortIcon("currency") && (
                            <FontAwesomeIcon
                              icon={getSortIcon("currency")}
                              className="w-4 pl-2"
                            />
                          )}
                        </th>
                        <th className="text-center"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredTariffs.map((tariff, index) => (
                        <tr key={index}>
                          <td
                            className="cursor-pointer"
                            onClick={() => {
                              navigate(`/tariffs/${tariff.tariffPublicId}`);
                            }}
                          >
                            {tariff.tariffPublicId}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() => {
                              navigate(`/tariffs/${tariff.tariffPublicId}`);
                            }}
                          >
                            {tariff.tariffName}
                          </td>
                          {showOrganization && (
                            <td
                              className="cursor-pointer"
                              onClick={() => {
                                navigate(`/tariffs/${tariff.tariffPublicId}`);
                              }}
                            >
                              {getOrganizationDisplayName(tariff) || "-"}
                            </td>
                          )}
                          <td
                            className="cursor-pointer"
                            onClick={() => {
                              navigate(`/tariffs/${tariff.tariffPublicId}`);
                            }}
                          >
                            {tariff.countryCode}
                          </td>
                          <td
                            className="cursor-pointer"
                            onClick={() => {
                              navigate(`/tariffs/${tariff.tariffPublicId}`);
                            }}
                          >
                            {tariff.currency}
                          </td>

                          <td className="text-center flex justify-end">
                            <CustomDropdown
                              isRight={true}
                              ContentComponent={() => {
                                return (
                                  <div className="w-40 flex flex-col items-start bg-white p-2 rounded-lg">
                                    <button
                                      className="block px-4 py-2 text-sm text-black hover:bg-mute-200 w-full text-left rounded-lg"
                                      onClick={() => {
                                        navigate(
                                          `/tariffs/${tariff.tariffPublicId}`
                                        );
                                      }}
                                    >
                                      <FontAwesomeIcon icon={faPenToSquare} />{" "}
                                      {t("common.edit")}
                                    </button>
                                    <button
                                      className="block px-4 py-2 text-sm text-red-500 hover:bg-mute-200 w-full text-left rounded-lg"
                                      onClick={() => { setConfirmState({ isOpen: true, title: t("confirm.deleteEntity", { entity: t("entity.tariff"), name: tariff.tariffName }), confirm: () => { handleDeleteTariff(tariff.tariffPublicId) } }) }}
                                    >
                                      <FontAwesomeIcon icon={faCircleMinus} /> {t("common.delete")}
                                    </button>
                                  </div>
                                );
                              }}
                            >
                              <div className="w-8 h-6 flex justify-end bg-red items-center cursor-pointer">
                                <FontAwesomeIcon icon={faEllipsis} />
                              </div>
                            </CustomDropdown>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
        }}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default TariffsPage;
